import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Star, Users2, Mail, Wallet, TrendingUp, UserPlus, Search, RefreshCw, ArrowLeft, ArrowRight } from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { formatCurrency } from "../../../utils/function";
import DealCard from "./deal-card";
import DealDetailsDrawer from "./deal-details-drawer";
import AddDealModal from "./add-deal-modal";
import { DealBoardItem, DealBoardColumn, DealsDashboard, DealStage } from "./types";
import { useStageChange } from "./use-stage-change";

// Deals Dashboard — wired to docs/deals-ui-integration.md. The dashboard
// request supplies the number cards, pipeline strip, and each board
// column's first page; per-column "Load more" and every other interaction
// (add/edit/move stage) use the existing deal CRUD endpoints, same as
// customers/details/deals.tsx's Deals tab.

const NUMBER_ICONS = [Star, Users2, Mail, Wallet, TrendingUp];
const STAGE_PAGE_SIZE = 20;
const STAGE_PAGE_MAX = 100; // per_stage_limit's documented ceiling

const formatMoney = (m?: { amount: string; currency: string } | null) =>
  m ? formatCurrency(Number(m.amount), m.currency, "symbol") : "—";

const DealsDashboardPage: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [dashboard, setDashboard] = useState<DealsDashboard | null>(null);
  const [board, setBoard] = useState<DealBoardColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [perStageLimit, setPerStageLimit] = useState(STAGE_PAGE_SIZE);
  const [loadingMoreStage, setLoadingMoreStage] = useState<DealStage | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [selectedDealFallback, setSelectedDealFallback] = useState<DealBoardItem | null>(null);
  const [dragOverStage, setDragOverStage] = useState<DealStage | null>(null);

  // Board slides via the ‹ › arrows only — the row is overflow-hidden (no
  // scrollbar, no drag), we just move scrollLeft programmatically.
  const boardRef = useRef<HTMLDivElement>(null);
  const [canSlideLeft, setCanSlideLeft] = useState(false);
  const [canSlideRight, setCanSlideRight] = useState(false);

  const updateArrows = () => {
    const el = boardRef.current;
    if (!el) return;
    setCanSlideLeft(el.scrollLeft > 4);
    setCanSlideRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  const slideBoard = (dir: -1 | 1) => {
    const el = boardRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(288, el.clientWidth * 0.8), behavior: "smooth" });
  };

  const { makeRequest: getDashboard } = useCrmApi("/deals/dashboard", "GET", { Authorization: `Bearer ${token}` });

  // The one place a currency code is an *input* — it tells /deals/dashboard
  // which currency to compute the two monetary summary cards in. Per the
  // ask, this now comes from the org's own wallet_currency (GET
  // /auth/get_userdata) instead of a hardcoded code; everything that
  // *displays* money still reads the currency back off the dashboard
  // response itself (value_currency / each MoneyMetric.currency / each
  // deal's own currency), never a hardcoded fallback.
  const { makeRequest: getUserData } = useRequest("/auth/get_userdata", "GET", { Authorization: `Bearer ${token}` });
  const [walletCurrency, setWalletCurrency] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [response] = await getUserData();
      const currency = response?.payload?.wallet_currency;
      if (currency) setWalletCurrency(currency);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setPerStageLimit(STAGE_PAGE_SIZE);
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(handle);
  }, [search]);

  const fetchDashboard = async (limit = perStageLimit) => {
    setLoading(true);
    setError(null);
    const params: Record<string, any> = { per_stage_limit: limit };
    if (walletCurrency) params.currency = walletCurrency;
    if (debouncedSearch) params.q = debouncedSearch;
    const [response, status] = await getDashboard(undefined, params);
    setLoading(false);
    setLoadingMoreStage(null);
    if (status < 200 || status >= 300) {
      setError(response?.detail || response?.message || "Failed to load the deals dashboard.");
      return;
    }
    setDashboard(response);
    setBoard(response?.board || []);
  };

  useEffect(() => {
    fetchDashboard(perStageLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, perStageLimit, walletCurrency]);

  // Recompute arrow enabled/disabled whenever the board content or the
  // viewport width changes.
  useEffect(() => {
    updateArrows();
    const onResize = () => updateArrows();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  const numberCards = useMemo(() => {
    if (!dashboard) return [];
    return [
      { label: "New Deals", value: dashboard.summary.new_deals, description: "All new deal opportunities currently in your pipeline" },
      {
        label: "Qualified Deals",
        value: dashboard.summary.qualified_deals,
        description: "Opportunities with strong purchase intent and enough context to pursue",
      },
      { label: "Proposals Sent", value: dashboard.summary.proposals_sent, description: "Deals where a proposal or offer has already been shared with the customer" },
      { label: "Avg. Deal Value", value: formatMoney(dashboard.summary.average_deal_value), description: "Average estimated value of the opportunities currently in your pipeline" },
      { label: "Potential Revenue", value: formatMoney(dashboard.summary.potential_revenue), description: "Total estimated revenue from all active customer opportunities" },
    ];
  }, [dashboard]);

  const handleStageChanged = (dealId: string, nextStage: DealStage) => {
    setBoard((prev) => {
      let moved: DealBoardItem | null = null;
      const withoutDeal = prev.map((col) => {
        const found = col.results.find((d) => d.id === dealId);
        if (!found) return col;
        moved = { ...found, stage: nextStage };
        return { ...col, total: Math.max(0, col.total - 1), results: col.results.filter((d) => d.id !== dealId) };
      });
      if (!moved) return prev;
      // "lost" isn't a board column — the deal just drops off the board.
      return withoutDeal.map((col) =>
        col.stage === nextStage ? { ...col, total: col.total + 1, results: [moved as DealBoardItem, ...col.results] } : col
      );
    });
    // Reconcile summary/pipeline numbers in the background — they depend on
    // stage counts across the whole tenant, not just this page's board.
    fetchDashboard();
  };

  // One shared instance drives every column's drop target — unlike each
  // card's own StageMenu (which already knows its fixed dealId), a drop can
  // land on any card, so the dealId is only known once the drop handler
  // reads it off the drag payload. Same real PATCH /deals/{id}/stage
  // request and the same "won"/"lost" extra-info prompts either way.
  const { requestStageChange, modals: stageChangeModals } = useStageChange(handleStageChanged);

  // Plain native HTML5 drag-and-drop — mirrors ticket/index.tsx's Column,
  // the proven-working reference for this exact pattern in this codebase.
  const DEAL_DRAG_TYPE = "dealId";
  const DEAL_DRAG_STAGE = "dealStage";

  const handleColumnDrop = (e: React.DragEvent<HTMLDivElement>, targetStage: DealStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const dealId = e.dataTransfer.getData(DEAL_DRAG_TYPE);
    const fromStage = e.dataTransfer.getData(DEAL_DRAG_STAGE) as DealStage;
    if (dealId && fromStage) requestStageChange(dealId, fromStage, targetStage);
  };

  // Same onDragOver/onDrop for both the column's own background and every
  // card inside it — a column with existing deals has no bare background
  // left to drop onto between/around the cards, so each card needs these
  // too rather than relying on the drop bubbling up through it.
  const columnDropProps = (stage: DealStage): React.HTMLAttributes<HTMLDivElement> => ({
    onDragOver: (e) => {
      e.preventDefault();
      setDragOverStage(stage);
    },
    onDrop: (e) => handleColumnDrop(e, stage),
  });

  // The plain GET /deals list endpoint doesn't return the enriched `person`
  // sub-object the board cards need (only the dashboard response does per
  // the doc), so "Load more" re-requests the dashboard with a larger
  // per_stage_limit instead of paginating the list endpoint — every column
  // grows together, capped at the documented 100 ceiling.
  const handleLoadMore = (col: DealBoardColumn) => {
    if (perStageLimit >= STAGE_PAGE_MAX) {
      toast(`Showing the maximum ${STAGE_PAGE_MAX} deals per column — narrow your search to see more.`);
      return;
    }
    setLoadingMoreStage(col.stage);
    setPerStageLimit((prev) => Math.min(prev + STAGE_PAGE_SIZE, STAGE_PAGE_MAX));
  };

  const openDetails = (deal: DealBoardItem) => {
    setSelectedDealFallback(deal);
    setSelectedDealId(deal.id);
  };

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <RefreshCw size={22} className="animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        <button
          type="button"
          onClick={() => fetchDashboard()}
          className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700"
        >
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Deals</h1>
          <p className="text-sm text-gray-500 font-semibold dark:text-gray-400 mt-0.5">Track purchase intent and individual customer opportunities</p>
        </div>

        {/* Deals in numbers */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Deals in numbers</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
            See a snapshot of the contacts and customer records currently available from your uploaded data.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
            {numberCards.map((stat, i) => {
              const Icon = NUMBER_ICONS[i] || Star;
              return (
                <div key={stat.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
                    <Icon size={15} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
                    <span className="truncate">{stat.label}</span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{stat.value}</p>
                  <p className="text-[12px] text-gray-500 dark:text-gray-100 font-semibold mt-1 leading-relaxed">{stat.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Pipeline strip */}
        {dashboard && dashboard.pipeline.length > 0 && (
          <section className="bg-indigo-50/60 dark:bg-indigo-500/10 rounded-xl border border-indigo-100 dark:border-indigo-500/20 p-4 sm:p-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {dashboard.pipeline.map((s) => (
                <div key={s.stage} className="min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-200 mb-1 truncate" title={s.label}>{s.label}</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    <span className="text-indigo-600 dark:text-indigo-400 underline decoration-2 underline-offset-4">{s.count}</span>
                    {s.percentage_of_new_deals != null && (
                      <span className="text-sm text-gray-400 dark:text-gray-500 font-normal ml-1.5">{s.percentage_of_new_deals}%</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Deal Flow Board */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Deal Flow Board</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Deals you can track and follow-up</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search deals"
                  className="pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20"
                />
              </div>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <UserPlus size={14} /> Add New Deal
              </button>
              <button
                type="button"
                onClick={() => fetchDashboard()}
                title="Refresh"
                className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
              <button
                type="button"
                onClick={() => slideBoard(-1)}
                disabled={!canSlideLeft}
                aria-label="Scroll board left"
                className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={15} />
              </button>
              <button
                type="button"
                onClick={() => slideBoard(1)}
                disabled={!canSlideRight}
                aria-label="Scroll board right"
                className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowRight size={15} />
              </button>
            </div>
          </div>

          {/* Kanban — no scrollbar / no drag-scroll; the ‹ › arrows above
              drive scrollLeft, disabling themselves at each edge. */}
          <div ref={boardRef} onScroll={updateArrows} className="flex gap-4 overflow-hidden py-1">
            {board.map((col) => (
              <div key={col.stage} className="w-[272px] shrink-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{col.label}</span>
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center shrink-0">
                    {col.total}
                  </span>
                </div>
                <div
                  {...columnDropProps(col.stage)}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragOverStage(null);
                  }}
                  className={`space-y-3 min-h-[60px] rounded-xl transition-colors ${
                    dragOverStage === col.stage ? "ring-2 ring-indigo-300 dark:ring-indigo-500/50 bg-indigo-50/40 dark:bg-indigo-500/5" : ""
                  }`}
                >
                  {col.results.length === 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 rounded-lg border border-dashed border-gray-200 dark:border-gray-800 p-4 text-center">
                      No deals at this stage.
                    </p>
                  ) : (
                    col.results.map((deal) => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        valueCurrency={dashboard?.value_currency}
                        onViewDetails={openDetails}
                        onStageChanged={handleStageChanged}
                        dropHandleProps={columnDropProps(col.stage)}
                        dragHandleProps={{
                          draggable: true,
                          onDragStart: (e) => {
                            e.dataTransfer.setData(DEAL_DRAG_TYPE, deal.id);
                            e.dataTransfer.setData(DEAL_DRAG_STAGE, deal.stage);
                            e.dataTransfer.effectAllowed = "move";
                          },
                        }}
                      />
                    ))
                  )}
                  {col.has_more && (
                    <button
                      type="button"
                      onClick={() => handleLoadMore(col)}
                      disabled={loadingMoreStage === col.stage}
                      className="w-full text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-60 py-1"
                    >
                      {loadingMoreStage === col.stage ? "Loading…" : "Load more"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <DealDetailsDrawer
        dealId={selectedDealId}
        fallback={selectedDealFallback}
        valueCurrency={dashboard?.value_currency}
        onClose={() => setSelectedDealId(null)}
        onStageChanged={handleStageChanged}
        onUpdated={fetchDashboard}
      />

      <AddDealModal isOpen={addOpen} onClose={() => setAddOpen(false)} onCreated={fetchDashboard} />

      {stageChangeModals}
    </div>
  );
};

export default DealsDashboardPage;
